/**
 * Утилиты для валидации форм акций
 */

/**
 * Валидация ссылки в зависимости от типа
 * @param {string} link - Ссылка для валидации
 * @param {boolean} isExternal - Является ли ссылка внешней
 * @returns {string|null} - Сообщение об ошибке или null
 */
export const validateLink = (link, isExternal) => {
  if (!link || link.trim() === '') {
    return 'Ссылка обязательна для заполнения';
  }

  if (isExternal) {
    // Валидация внешней ссылки (должна начинаться с http:// или https://)
    const urlPattern = /^https?:\/\/.+/;
    if (!urlPattern.test(link)) {
      return 'Внешняя ссылка должна начинаться с http:// или https://';
    }
  } else {
    // Валидация внутренней ссылки (должна начинаться с /)
    if (!link.startsWith('/')) {
      return 'Внутренняя ссылка должна начинаться с /';
    }
  }

  return null; // Нет ошибки
};

/**
 * Валидация дат начала и окончания
 * @param {Date} startDate - Дата начала
 * @param {Date} endDate - Дата окончания
 * @returns {Object} - Объект с ошибками
 */
export const validateDates = (startDate, endDate) => {
  const errors = {};
  
  if (!startDate) {
    errors.date_start = 'Дата начала обязательна для заполнения';
  }
  
  if (!endDate) {
    errors.date_end = 'Дата окончания обязательна для заполнения';
  }

  if (startDate) {
    const now = new Date();
    if (new Date(startDate) <= now) {
      errors.date_start = 'Дата начала не может быть в прошлом';
    }
  }
  
  if (startDate && endDate) {
    if (new Date(endDate) <= new Date(startDate)) {
      errors.date_end = 'Дата окончания должна быть позже даты начала';
    }
  }
  
  return errors;
};

/**
 * Валидация городов
 * @param {Array} cities - Массив выбранных городов
 * @returns {string|null} - Сообщение об ошибке или null
 */
export const validateCities = (cities) => {
  if (!cities || cities.length === 0) {
    return 'Необходимо выбрать хотя бы один город';
  }
  return null;
};

/**
 * Валидация файлов для списка клиентов
 * @param {string} availabilityType - Тип доступности акции
 * @param {Array} fileIds - Массив ID загруженных файлов
 * @returns {string|null} - Сообщение об ошибке или null
 */
export const validateFileIds = (availabilityType, fileIds) => {
  if (availabilityType === 'customer_id') {
    if (!fileIds || fileIds.length === 0) {
      return 'Необходимо загрузить файл со списком клиентов';
    }
  }
  return null;
};

/**
 * Валидация инициатора
 * @param {Object} selectedInitiator - Выбранный инициатор
 * @param {Object} initiatorData - Данные нового инициатора
 * @returns {Object} - Объект с ошибками
 */
export const validateInitiator = (selectedInitiator, initiatorData) => {
  const errors = {};
  
  const hasSelectedInitiator = selectedInitiator && selectedInitiator.value;
  const hasNewInitiatorData = initiatorData && (
    initiatorData.name?.trim() || 
    initiatorData.surname?.trim() || 
    initiatorData.department?.trim() || 
    initiatorData.phone?.trim()
  );
  
  if (!hasSelectedInitiator && !hasNewInitiatorData) {
    errors.initiator = 'Необходимо выбрать инициатора из списка или заполнить данные для создания нового';
    return errors;
  }
  
  // Если заполняются данные нового инициатора, проверяем обязательные поля
  if (hasNewInitiatorData && !hasSelectedInitiator) {
    if (!initiatorData.name?.trim()) {
      errors.initiator_name = 'Имя инициатора обязательно для заполнения';
    }
    if (!initiatorData.surname?.trim()) {
      errors.initiator_surname = 'Фамилия инициатора обязательна для заполнения';
    }
    if (!initiatorData.department?.trim()) {
      errors.initiator_department = 'Отдел инициатора обязателен для заполнения';
    }
    if (!initiatorData.phone?.trim()) {
      errors.initiator_phone = 'Телефон инициатора обязателен для заполнения';
    }
  }
  
  return errors;
};

/**
 * Валидация обязательных текстовых полей для всех языков
 * @param {Object} formData - Данные формы
 * @returns {Object} - Объект с ошибками валидации
 */
export const validateRequiredFields = (formData) => {
  const errors = {};
  const languages = ['ru', 'kk', 'en'];
  
  // Валидация названия акции (promotion_name)
  languages.forEach(lang => {
    const fieldName = `name_${lang}`;
    const value = formData[fieldName];
    if (!value || (typeof value === 'string' && value.trim() === '')) {
      errors[fieldName] = `Название акции (${lang.toUpperCase()}) обязательно для заполнения`;
    }
  });
  
  // Валидация краткого описания (short_description)
  languages.forEach(lang => {
    const fieldName = `short_description_${lang}`;
    const value = formData[fieldName];
    if (!value || (typeof value === 'string' && value.trim() === '')) {
      errors[fieldName] = `Краткое описание (${lang.toUpperCase()}) обязательно для заполнения`;
    }
  });
  
  // Валидация полного описания (description) - EditorState
  languages.forEach(lang => {
    const fieldName = `full_description_${lang}`;
    const editorState = formData[fieldName];
    if (!editorState) {
      errors[fieldName] = `Полное описание (${lang.toUpperCase()}) обязательно для заполнения`;
    } else {
      // Проверяем, что EditorState не пустой
      const contentState = editorState.getCurrentContent();
      const plainText = contentState.getPlainText().trim();
      if (plainText === '') {
        errors[fieldName] = `Полное описание (${lang.toUpperCase()}) обязательно для заполнения`;
      }
    }
  });
  
  // Валидация баннера (banner_url)
  languages.forEach(lang => {
    const fieldName = `banner_${lang}`;
    const value = formData[fieldName];
    if (!value || (typeof value === 'string' && value.trim() === '')) {
      errors[fieldName] = `Баннер (${lang.toUpperCase()}) обязателен для загрузки`;
    }
  });
  
  return errors;
};

/**
 * Валидация кнопок
 * @param {boolean} showButtons - Показывать ли кнопки
 * @param {Array} buttons - Массив кнопок
 * @returns {Object} - Объект с ошибками по индексам кнопок
 */
export const validateButtons = (showButtons, buttons) => {
  const errors = {};
  
  if (showButtons && buttons) {
    buttons.forEach((button, index) => {
      const linkError = validateLink(button.link, button.is_external_link);
      if (linkError) {
        errors[`${index}_link`] = linkError;
      }
    });
  }
  
  return errors;
};

/**
 * Комплексная валидация всей формы
 * @param {Object} formData - Данные формы
 * @param {string} mode - Режим: 'create', 'edit', 'copy'
 * @returns {Object} - Объект со всеми ошибками валидации
 */
export const validatePromotionForm = (formData, mode = 'create') => {
  const allErrors = {};
  
  // Валидация обязательных полей (promotion_name, short_description, description, banner_url)
  const requiredFieldsErrors = validateRequiredFields(formData);
  Object.assign(allErrors, requiredFieldsErrors);
  
  // Валидация дат
  const dateErrors = validateDates(formData.date_start, formData.date_end);

  if (mode !== 'edit') {
    Object.assign(allErrors, dateErrors);
  }
  
  // Валидация городов
  const citiesError = validateCities(formData.cities);
  if (citiesError) {
    allErrors.cities = citiesError;
  }
  
  // Валидация файлов
  const fileError = validateFileIds(formData.availability_type, formData.file_ids);
  if (fileError) {
    allErrors.file_ids = fileError;
  }
  
  // Валидация инициатора
  const initiatorErrors = validateInitiator(formData.selected_initiator, formData.initiator);
  Object.assign(allErrors, initiatorErrors);
  
  // Валидация кнопок
  const buttonErrors = validateButtons(formData.show_buttons, formData.buttons);
  
  return {
    formErrors: allErrors,
    buttonErrors: buttonErrors,
    dateErrors: dateErrors
  };
};
